import {Component, computed, effect, inject, signal, viewChild, WritableSignal} from '@angular/core';
import {
  AddToCartComponent,
  artworkFilters,
  FilterComponent,
  LeftContainerComponent,
  LikeButtonComponent,
  MediaRenderComponent,
  PageContainerComponent,
  RightContainerComponent
} from "@components";
import {CurrencyPipe} from "@angular/common";
import {ArtworkService} from "@api-services";
import {ArtworkDto, SearchArtworkRequestDto} from "@api-contracts";
import {debouncedSignal} from "../../../utils";
import {RouterLink} from "@angular/router";
import {rxResource, toSignal} from "@angular/core/rxjs-interop";
import {catchError, map, tap} from "rxjs/operators";
import {of} from "rxjs";
import {ArtworkArtistPipe} from "../../../pipes/display-name.pipe";
import {CookieService} from "../../../components/cookie-policy-modal/cookie-policy-modal.component";

@Component({
  selector: 'wun-artworks',
  imports: [
    FilterComponent,
    CurrencyPipe,
    AddToCartComponent,
    LeftContainerComponent,
    RightContainerComponent,
    RouterLink,
    PageContainerComponent,
    MediaRenderComponent,
    LikeButtonComponent,
    ArtworkArtistPipe
  ],
  templateUrl: './artworks.component.html',
  styleUrl: './artworks.component.scss'
})
export class ArtworksComponent {
  private readonly cookieService = inject(CookieService)
  filter = viewChild(FilterComponent);
  filterSignal: WritableSignal<artworkFilters | undefined> = signal({
    price: {
      from: 0,
      to: 1000000000
    },
    categories: [],
    subCategories: [],
    name: '',
    type: undefined
  });

  filterDebounce = debouncedSignal(this.filterSignal, 400)

  artworkService = inject(ArtworkService);

  _artworks = rxResource({
    params: this.filterDebounce,
    stream: (param) => {
      return (this.search(param.params))
    }
  })
  artworks = signal<ArtworkDto[]>([])

  /** Highest price among published artworks → upper bound of the price slider. */
  maxPrice = toSignal(
    this.artworkService.artworksFindAndPaginate({
      filters: JSON.stringify({status: 'EVALUATION_ACCEPTED'}),
      sorting: JSON.stringify({price: 'DESC'}),
      pageSize: 1,
    }).pipe(map(d => Number(d.data[0]?.price) || 0), catchError(() => of(0))),
    {initialValue: 0}
  );

  /** Cities of the artists currently listed, suggested in the location filter. */
  private readonly knownCities = signal<string[]>([]);
  locations = this.knownCities.asReadonly();
  private debounceTime: number | undefined;
  _ = computed(() => {
    const _ = this.filter();
    return _?.filter();
  })

  init: boolean = true;

  constructor() {
    effect(() => {
      const filter = this.filter();
      if (filter) this.setFilters(filter.filter())
    });
  }

  setFilters(filters: artworkFilters) {
    clearTimeout(this.debounceTime)
    this.debounceTime = setTimeout(() => {
      if (!this.init)
        this.filterSignal.set(filters);
      else
        this.init = false;
    }, 300) as any;
  }

  search(request = this.filterSignal()) {
    const filters: SearchArtworkRequestDto = {
      status: 'EVALUATION_ACCEPTED'
    }
    const and: any[] = [];
    if (request?.name?.trim()) {
      // Free text: title, artist name and tags (handled by the backend).
      (filters as any).$search = request.name.trim();
    }

    if (request?.mediums?.length) {
      (filters as any).technique = {$in: request.mediums};
    }

    if (request?.location) {
      and.push({
        $or: [
          {artist: {city: {$ilike: `%${request.location}%`}}},
          {artist: {country: {$ilike: `%${request.location}%`}}},
        ]
      });
    }

    if (request?.price) {
      (filters as any).price = {
        $gte: request.price.from,
        $lte: request.price.to,
      }
    }


    if (request?.size?.height?.from || request?.size?.height?.to) {
      (filters as any).height = {
        ...(request.size.height.from ? {$gte: request.size.height.from} : {}),
        ...(request.size.height.to ? {$lte: request.size.height.to} : {}),
      }
    }

    if (request?.size?.width?.from || request?.size?.width?.to) {
      (filters as any).width = {
        ...(request.size.width.from ? {$gte: request.size.width.from} : {}),
        ...(request.size.width.to ? {$lte: request.size.width.to} : {}),
      }
    }

    if (and.length) (filters as any).$and = and;

    return this.artworkService.artworksFindAndPaginate(
      {
        filters: JSON.stringify(filters),
        includes: '["mainAttachment","artist","attachments"]',
        pageSize: 200,
        sorting: JSON.stringify(
          request?.sortBy === 'mostLiked' ? {wishesCount: 'DESC'} :
          request?.sortBy === 'recent' ? {updatedAt: 'DESC'} :
          {score: 'DESC'}
        )
      }
    ).pipe(tap(d => {
      if(request?.name)
        this.cookieService.search(request?.name)
      this.artworks.set(d.data)
      const cities = new Set(this.knownCities());
      d.data.forEach(a => a.artist?.city?.trim() && cities.add(a.artist.city.trim()));
      if (cities.size !== this.knownCities().length)
        this.knownCities.set([...cities].sort((a, b) => a.localeCompare(b)));
    }))

  }
}
